/**
 * ReCaptcha V3 Component
 *
 * Google reCAPTCHA v3 wrapper for invisible bot detection
 * Unlike v2, v3 runs in the background without user interaction
 *
 * Usage:
 * ```tsx
 * const { executeRecaptcha } = useReCaptchaV3();
 *
 * const handleSubmit = async () => {
 *   const token = await executeRecaptcha('submit_form');
 *   // Use token in your API call
 * };
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { env } from '@/config/env';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * Hook to use reCAPTCHA v3
 * @returns executeRecaptcha function to get token for an action
 */
export function useReCaptchaV3() {
  const loadedRef = useRef(false);
  const siteKey = env.VITE_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (!siteKey) {
      console.error('ReCAPTCHA site key not configured. Please set VITE_RECAPTCHA_SITE_KEY in environment variables.');
      return;
    }

    if (loadedRef.current) return;

    // Load reCAPTCHA v3 script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    loadedRef.current = true;

    return () => {
      // Cleanup: remove script if component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [siteKey]);

  /**
   * Execute reCAPTCHA and get token
   * @param action - The action name (e.g., 'login', 'signup', 'submit')
   * @returns Promise resolving to reCAPTCHA token or null if error
   */
  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | null> => {
      if (!siteKey) {
        console.error('ReCAPTCHA site key not configured');
        return null;
      }

      try {
        return await new Promise((resolve) => {
          if (typeof window.grecaptcha === 'undefined') {
            console.error('reCAPTCHA not loaded');
            resolve(null);
            return;
          }

          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute(siteKey, { action });
              resolve(token);
            } catch (error) {
              console.error('reCAPTCHA execution error:', error);
              resolve(null);
            }
          });
        });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        return null;
      }
    },
    [siteKey]
  );

  return { executeRecaptcha };
}
